import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TuneIcon from '@mui/icons-material/Tune';
import CategoryIcon from '@mui/icons-material/Category';

// Components
import Loading from '../../../components/global/loading/Loading';

// Hooks & Context
import { useProduct } from '../../../context/ProductContext';
import { useCategory } from '../../../context/CategoryContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { Product } from '../../../models/Product';
import { Category } from '../../../models/Category';
import Button from '../../../components/formElements/Button';
import AddProduct from './AddProduct';
import EditProduct from './EditProduct';
import AdminProductCard from '../../../components/AdminProductCard';

// Niestandardowy komponent przycisku
const CustomButton = ({
  to,
  onClick,
  className = '',
  variant = 'primary',
  disabled = false,
  children,
}: {
  to?: string;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  children: React.ReactNode;
}) => {
  // Wspólne style bazowe
  const baseStyles = `
    font-medium text-sm rounded-md transition-all duration-200
    flex items-center justify-center gap-2 whitespace-nowrap
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `;

  // Style dla różnych wariantów
  const variantStyles = {
    primary: `bg-primary hover:bg-primary-dark text-white py-2 px-4
              shadow-sm hover:shadow hover:translate-y-[-1px] active:translate-y-[0px]`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4`,
    outline: `border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 px-4`,
  };

  // Łączymy wszystkie style
  const buttonStyles = `${baseStyles} ${variantStyles[variant]}`;

  // Jeśli mamy atrybut "to", renderujemy Link, w przeciwnym razie button
  if (to) {
    return (
      <Link to={to} className={buttonStyles}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} className={buttonStyles}>
      {children}
    </button>
  );
};

function ProductsList() {
  const navigate = useNavigate();
  const { control } = useForm();

  // Stan wyszukiwania i filtrowania
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  // Stany dla produktów i kategorii
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Konteksty
  const { getProducts } = useProduct();
  const { getCategories } = useCategory();
  const debouncedQuery = useDebounce(query, 500);

  // Definicja stanu jako typ enum
  type ProductStatus = 'all' | 'draft' | 'published';
  const [productStatus, setProductStatus] = useState<ProductStatus>('all');

  // Pobieranie produktów
  const getProductsData = useCallback(
    async (page: number, query: string) => {
      setIsLoading(true);
      try {
        const response = await getProducts({
          page,
          limit: 10,
          query,
          categoryId: selectedCategory,
          draft:
            productStatus === 'draft'
              ? true
              : productStatus === 'published'
                ? false
                : undefined,
        });
        setProducts(response.products);
        setTotalProducts(response.totalProducts);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
      } finally {
        setIsLoading(false);
      }
    },
    [productStatus, getProducts, selectedCategory],
  );

  // Pobieranie kategorii do filtrowania
  const getCategoriesData = useCallback(async () => {
    try {
      const response = await getCategories();
      setCategories(response.categories);
    } catch (error) {
      console.error('Błąd podczas ładowania kategorii:', error);
    }
  }, [getCategories]);

  // Efekty dla pobierania danych
  useEffect(() => {
    getCategoriesData();
  }, [getCategoriesData]);

  useEffect(() => {
    getProductsData(currentPage, debouncedQuery);
  }, [debouncedQuery, productStatus, currentPage, getProductsData]);

  // Funkcje obsługi zdarzeń
  const handleClearSearch = () => {
    setQuery('');
  };

  const handleCategoryChange = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const toggleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="flex flex-col items-stretch space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingBagIcon className="text-primary" />
            Produkty
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Zarządzaj produktami dostępnymi w sklepie
          </p>
        </motion.div>

        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="self-end sm:self-auto flex gap-2"
        >
          <CustomButton to="/admin/produkty/dodaj" variant="primary">
            <AddIcon fontSize="small" />
            <span>Dodaj produkt</span>
          </CustomButton>

          <CustomButton onClick={toggleShowFilters} variant="outline">
            <FilterListIcon fontSize="small" />
            <span>Filtry</span>
          </CustomButton>
        </motion.div>
      </div>

      {/* Search bar */}
      <motion.div
        className="grid grid-cols-1 gap-4"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          <Controller
            name="search"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  {...field}
                  placeholder="Szukaj produktu (nazwa, kod, opis)"
                  className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <ClearIcon
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
                    fontSize="small"
                    onClick={handleClearSearch}
                  />
                )}
              </div>
            )}
          />
        </div>
      </motion.div>

      {/* Filtry */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-medium flex items-center gap-1">
                  <TuneIcon fontSize="small" className="text-gray-500" />
                  <span>Filtruj produkty</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Filtr kategorii */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategoria
                  </label>
                  <div className="bg-white p-2 max-h-64 overflow-y-auto border border-gray-200 rounded">
                    <div
                      className={`flex items-center p-2 rounded cursor-pointer ${
                        selectedCategory === null
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleCategoryChange(null)}
                    >
                      <CategoryIcon fontSize="small" className="mr-2" />
                      <span>Wszystkie kategorie</span>
                    </div>
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className={`flex items-center p-2 rounded cursor-pointer ${
                          selectedCategory === category.id
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        <span className="ml-2">{category.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Inne filtry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status produktu
                  </label>
                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                    <div
                      onClick={() => setProductStatus('all')}
                      className={`flex items-center justify-between p-3 cursor-pointer border-b border-gray-100 ${
                        productStatus === 'all'
                          ? 'bg-primary/10'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            productStatus === 'all'
                              ? 'bg-primary'
                              : 'bg-gray-300'
                          }`}
                        ></div>
                        <span
                          className={`text-sm ${
                            productStatus === 'all'
                              ? 'font-medium text-primary'
                              : 'text-gray-700'
                          }`}
                        >
                          Wszystkie produkty
                        </span>
                      </div>
                      {productStatus === 'all' && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                          Aktywne
                        </span>
                      )}
                    </div>

                    <div
                      onClick={() => setProductStatus('published')}
                      className={`flex items-center justify-between p-3 cursor-pointer border-b border-gray-100 ${
                        productStatus === 'published'
                          ? 'bg-primary/10'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            productStatus === 'published'
                              ? 'bg-primary'
                              : 'bg-gray-300'
                          }`}
                        ></div>
                        <span
                          className={`text-sm ${
                            productStatus === 'published'
                              ? 'font-medium text-primary'
                              : 'text-gray-700'
                          }`}
                        >
                          Wszystkie oprócz wersji roboczych
                        </span>
                      </div>
                      {productStatus === 'published' && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                          Aktywne
                        </span>
                      )}
                    </div>

                    <div
                      onClick={() => setProductStatus('draft')}
                      className={`flex items-center justify-between p-3 cursor-pointer ${
                        productStatus === 'draft'
                          ? 'bg-primary/10'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            productStatus === 'draft'
                              ? 'bg-primary'
                              : 'bg-gray-300'
                          }`}
                        ></div>
                        <span
                          className={`text-sm ${
                            productStatus === 'draft'
                              ? 'font-medium text-primary'
                              : 'text-gray-700'
                          }`}
                        >
                          Tylko wersje robocze
                        </span>
                      </div>
                      {productStatus === 'draft' && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                          Aktywne
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-2 pl-1">
                    {productStatus === 'all'
                      ? 'Pokazywane są wszystkie produkty, zarówno opublikowane jak i wersje robocze'
                      : productStatus === 'published'
                        ? 'Pokazywane są tylko produkty opublikowane, bez wersji roboczych'
                        : 'Pokazywane są tylko wersje robocze produktów'}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
                <Button
                  onClick={() => {
                    setProductStatus('all');
                    setSelectedCategory(null);
                    setCurrentPage(1);
                  }}
                  colorTheme="outline"
                  className="w-auto mr-2"
                >
                  Wyczyść filtry
                </Button>
                <Button
                  onClick={() => getProductsData(1, query)}
                  colorTheme="primary"
                  className="w-auto"
                >
                  Zastosuj filtry
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status bar */}
      <motion.div
        className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div>
          Wyświetlono <span className="font-semibold">{products.length}</span> z{' '}
          <span className="font-semibold">{totalProducts}</span> produktów
          {selectedCategory !== null && ' w wybranej kategorii'}
          {productStatus === 'draft'
            ? ' (tylko wersje robocze)'
            : productStatus === 'published'
              ? ' (bez wersji roboczych)'
              : ''}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span>
              Strona {currentPage} z {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() =>
                  currentPage > 1 && getProductsData(currentPage - 1, query)
                }
                disabled={currentPage <= 1}
                className={`p-1 rounded ${
                  currentPage <= 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon
                  style={{
                    fontSize: '0.9rem',
                    transform: 'rotate(180deg)',
                  }}
                />
              </button>
              <button
                onClick={() =>
                  currentPage < totalPages &&
                  getProductsData(currentPage + 1, query)
                }
                disabled={currentPage >= totalPages}
                className={`p-1 rounded ${
                  currentPage >= totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon style={{ fontSize: '0.9rem' }} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="loading"
            className="flex justify-center py-12"
          >
            <Loading />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="content"
            transition={{ duration: 0.3 }}
          >
            {products.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                      }}
                    >
                      <AdminProductCard product={product} key={product.id} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <ShoppingBagIcon
                  style={{ fontSize: '3rem' }}
                  className="text-gray-300 mb-2 mx-auto"
                />
                <h3 className="text-lg font-medium text-gray-700">
                  {query
                    ? 'Nie znaleziono produktów pasujących do zapytania'
                    : 'Brak produktów'}
                </h3>
                <p className="text-gray-500 mt-1">
                  {query
                    ? 'Spróbuj zmienić kryteria wyszukiwania lub filtry'
                    : 'Dodaj swój pierwszy produkt, aby rozpocząć'}
                </p>
                <CustomButton
                  to="/admin/produkty/dodaj"
                  className="mt-4 py-2.5 px-5"
                >
                  <AddIcon fontSize="small" />
                  <span>Dodaj produkt</span>
                </CustomButton>
              </div>
            )}

            {/* Paginacja */}
            {totalPages > 1 && (
              <div className="flex justify-center py-6">
                <div className="flex items-center gap-1">
                  <CustomButton
                    onClick={() => getProductsData(1, query)}
                    disabled={currentPage === 1}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === 1 ? 'opacity-50' : ''
                    }`}
                  >
                    Pierwsza
                  </CustomButton>

                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={idx}
                        onClick={() => getProductsData(pageNum, query)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                        ${
                          currentPage === pageNum
                            ? 'bg-primary text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <CustomButton
                    onClick={() => getProductsData(totalPages, query)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === totalPages ? 'opacity-50' : ''
                    }`}
                  >
                    Ostatnia
                  </CustomButton>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Podsumowanie produktów */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4"
      >
        <h3 className="text-lg font-semibold mb-3 pb-2 border-b border-gray-200 text-gray-800">
          Podsumowanie produktów
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
            <span className="block text-2xl font-bold text-blue-600">
              {totalProducts}
            </span>
            <span className="text-sm text-blue-600">Wszystkich produktów</span>
          </div>

          <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
            <span className="block text-2xl font-bold text-green-600">
              {products.filter((p) => !p.draft).length}
            </span>
            <span className="text-sm text-green-600">
              Produktów opublikowanych
            </span>
          </div>

          <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg">
            <span className="block text-2xl font-bold text-amber-600">
              {products.filter((p) => p.draft).length}
            </span>
            <span className="text-sm text-amber-600">Wersji roboczych</span>
          </div>

          <div className="bg-purple-50 border border-purple-100 p-4 rounded-lg">
            <span className="block text-2xl font-bold text-purple-600">
              {categories.length}
            </span>
            <span className="text-sm text-purple-600">
              Dostępnych kategorii
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-center sm:justify-end">
          <Button
            onClick={() => navigate('/admin/produkty/dodaj')}
            colorTheme="primary"
            className="w-auto"
          >
            <AddIcon fontSize="small" className="mr-1" />
            Dodaj nowy produkt
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Główny komponent zarządzający routingiem
function Products() {
  return (
    <Routes>
      <Route path="dodaj" element={<AddProduct />} />
      <Route path="edytuj/:id" element={<EditProduct />} />
      <Route path="" element={<ProductsList />} />
    </Routes>
  );
}

export default Products;
